import { useState } from "react";
import { Button, FileButton, Group, Image, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { ApiError } from "../api/client";
import { mediaUrl, type Show, shows } from "./api";

/**
 * The share link's preview image (`R-HOST-37`) — what WhatsApp, Facebook and the rest draw above
 * the link when a host posts it.
 *
 * **None by default.** A show without one previews as a plain card of title and time; nothing is
 * picked on the host's behalf. Uploading is immediate rather than part of the details form: it is
 * a file, not a field, and there is nothing to type before it can be saved.
 *
 * ## The crop happens here
 *
 * The chosen file is scaled to cover `WIDTH` × `HEIGHT`, centred, and sent as a JPEG — so what the
 * host sees below is exactly what a card shows, and the upload is a couple of hundred kilobytes
 * however large the original was. See `packages/core/src/shows/thumbnail.ts` for why the server
 * expects that.
 *
 * A messaging app keeps a preview it has already scraped for as long as it likes, so a new picture
 * reaches links that are posted afterwards; the note under the image says so.
 */

/** `THUMBNAIL_WIDTH` × `THUMBNAIL_HEIGHT` in `packages/core/src/shows/thumbnail.ts`. */
const WIDTH = 1200;
const HEIGHT = 630;

export function ShowThumbnail({
  show,
  mayEdit,
  onChanged,
}: {
  show: Show;
  /** The host's, while the show can still be edited — the same rule as the details form. */
  mayEdit: boolean;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(undefined);
    try {
      await action();
      await onChanged();
    } catch (failure) {
      setError(
        failure instanceof ApiError
          ? failure.message
          : failure instanceof ImageUnreadable
            ? t("shows.thumbnailUnreadable")
            : t("shows.unreachable"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack gap={6}>
      <Text size="xs" fw={600}>
        {t("shows.thumbnailLabel")}
      </Text>
      {show.thumbnailPath ? (
        <Image
          src={mediaUrl(show.thumbnailPath)}
          alt={t("shows.thumbnailAlt", { title: show.title })}
          w={240}
          h={126}
          radius="sm"
          fit="cover"
        />
      ) : (
        <Text size="xs" c="dimmed">
          {t("shows.thumbnailNone")}
        </Text>
      )}
      {mayEdit ? (
        <Group gap="xs">
          <FileButton
            accept="image/jpeg,image/png,image/webp"
            disabled={busy}
            onChange={(file) => {
              if (file) {
                void run(async () =>
                  shows.setThumbnail(show.id, await cropToCard(file)),
                );
              }
            }}
          >
            {(props) => (
              <Button {...props} size="compact-xs" variant="default" loading={busy}>
                {show.thumbnailPath
                  ? t("shows.thumbnailReplace")
                  : t("shows.thumbnailUpload")}
              </Button>
            )}
          </FileButton>
          {show.thumbnailPath ? (
            <Button
              size="compact-xs"
              variant="subtle"
              color="red"
              disabled={busy}
              onClick={() => void run(() => shows.removeThumbnail(show.id))}
            >
              {t("shows.thumbnailRemove")}
            </Button>
          ) : null}
        </Group>
      ) : null}
      {mayEdit ? (
        <Text size="xs" c="dimmed">
          {t("shows.thumbnailHelp")}
        </Text>
      ) : null}
      {error ? (
        <Text size="xs" c="red">
          {error}
        </Text>
      ) : null}
    </Stack>
  );
}

class ImageUnreadable extends Error {}

/**
 * The file, scaled to cover the card and centred, as a JPEG `data:` URL.
 *
 * `createImageBitmap` with `imageOrientation: "from-image"` so a phone photo taken upright is
 * upright here, which drawing an `<img>` does not promise on every browser.
 */
async function cropToCard(file: File): Promise<string> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    throw new ImageUnreadable();
  }
  const scale = Math.max(WIDTH / bitmap.width, HEIGHT / bitmap.height);
  const width = bitmap.width * scale;
  const height = bitmap.height * scale;

  const canvas = document.createElement("canvas");
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const context = canvas.getContext("2d");
  if (!context) throw new ImageUnreadable();
  // JPEG has no transparency; a PNG with a transparent background would otherwise turn black.
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, WIDTH, HEIGHT);
  context.drawImage(bitmap, (WIDTH - width) / 2, (HEIGHT - height) / 2, width, height);
  bitmap.close();
  return canvas.toDataURL("image/jpeg", 0.85);
}
